<template>
  <aside class="document-index relative mr-8 flex-shrink">
    <Teleport to="#document-page">
      <FontAwesomeIcon
        class="document-index-open-btn xl:hidden absolute top-[12px] -left-8 -mt-12 btn btn-outline btn-primary-alt rounded-full min-w-0 w-4 h-4 max-w-4 max-h-4 p-1 !border-chrome-light xl:hidden"
        title="Show editor navigation"
        :icon="faArrowRight"
        @click.stop="showDocumentIndex = true"
      />
    </Teleport>

    <Transition
      name="slide"
      :css="!isBigScreen"
    >
      <div
        v-if="showDocumentIndex"
        ref="sidebarRef"
        class="w-[270px]"
        :class="[
          isBigScreen ?
            'sticky top-1 mt-4' :
            'shadow-right-light bg-white absolute top-0 left-0 z-[999] border-t border-t-chrome-subtle',
        ]"
      >
        <div class="max-h-[calc(100vh-53px-1rem)] overflow-y-auto custom-scrollbar pb-12">
          <div class="flex justify-between items-center xl:hidden border-b border-b-chrome-light px-4 py-2">
            <h2 class="text-black">
              Editor Navigation
            </h2>

            <FontAwesomeIcon
              class="close-btn close-btn-primary w-[14px] h-[14px] p-1"
              :icon="faXmark"
              @click="showDocumentIndex = false"
            />
          </div>

          <ul class="text-sm">
            <template
              v-for="(pageItem, index) in pages"
              :key="index"
            >
              <li class="mb-1">
                <span
                  class="text-black px-4 lg:pl-10 py-1 cursor-pointer font-medium group block"
                  :class="{ 'bg-chrome-subtle': page === index }"
                  @click="handlePageClick(index)"
                >
                  {{ pageItem.name }}
                </span>

                <ul v-if="pageItem.sections && pageItem.sections.length">
                  <li
                    v-for="section in pageItem.sections"
                    :key="section.id"
                    class="px-8 lg:pl-14 py-0.5 cursor-pointer text-chrome-dark hover:bg-chrome-subtle"
                    @click="handleSectionClick(index, section.id)"
                  >
                    {{ section.name }}
                  </li>
                </ul>
              </li>
            </template>
          </ul>
        </div>
      </div>
    </transition>
  </aside>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const emit = defineEmits(['sectionIdChanged']);

defineProps({
  pages: {
    type: Array,
    default: () => [],
  },
});

const showDocumentIndex = ref(false);
const isBigScreen = ref(false);
const sidebarRef = ref(null);

const checkScreenSize = () => {
  const isXl = window.innerWidth >= 1280;

  if (isXl !== isBigScreen.value) {
    isBigScreen.value = isXl;
    showDocumentIndex.value = isXl;
  }
};

onMounted(() => {
  checkScreenSize();
  window.addEventListener('resize', checkScreenSize);
  window.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkScreenSize);
  window.removeEventListener('click', handleClickOutside);
});

const page = defineModel({ type: Number, default: 0 });

const handlePageClick = (pageIndex) => {
  page.value = pageIndex;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleSectionClick = async (pageIndex, sectionId) => {
  // If we are on a different page, switch pages first
  if (page.value === pageIndex) {
    // If on same page, just scroll
    scrollToId(sectionId);
  } else {
    page.value = pageIndex;

    // Wait for the new component to mount and render
    await nextTick();
    scrollToId(sectionId);
  }
};

const scrollToId = (sectionId) => {
  emit('sectionIdChanged', sectionId);

  const findAndScroll = (attempts = 0) => {
    const element = document.getElementById(sectionId);

    if (element) {
      // element exists, scroll and exit loop.
      const headerOffset = 200;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      });
    } else if (attempts < 50) {
      // element with id not loaded yet (maybe an api call is still running). retry every 100ms for 5s.
      setTimeout(() => findAndScroll(attempts + 1), 100);
    }
  };

  findAndScroll();
};

const handleClickOutside = (event) => {
  // If we are on big screen or sidebar is already closed, do nothing
  if (isBigScreen.value || !showDocumentIndex.value) return;

  // If sidebar exists and click target is NOT inside the sidebar, close it
  if (sidebarRef.value && !sidebarRef.value.contains(event.target)) {
    showDocumentIndex.value = false;
  }
};
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #E2E1E3;
  border-radius: 20px;
}

.slide-enter-active,
 .slide-leave-active {
   transition: transform 0.3s ease-in-out;
 }

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
